import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Connect } from "../../shared/components/connect/connect";
import { Background } from "../../shared/components/background/background";
import { HeroSection } from "../../shared/components/hero-section/hero-section";
import { Button } from "../../shared/components/button/button";
import { WhySemnexus } from "./components/why-semnexus/why-semnexus";
import { Services } from './components/services/services';
import { DevServices } from './components/dev-services/dev-services';
import { TagServices } from './components/tag-services/tag-services';
import { LeadSection } from './components/lead-section/lead-section';
import { CaseStudies } from './components/case-studies/case-studies';
import { Team } from './components/team/team';
import { PostCarouselContainer } from './components/post-carousel-container/post-carousel-container';
import { ActivatedRoute } from '@angular/router';
import { Brands } from './components/brands/brands';
import { ContentSection } from '../../shared/components/content-section/content-section';
import { CalendlyService } from '../../shared/services/calendly.service';
import { SeoService } from '../../shared/services/seo.service';
import {
  LANDING_INTRO,
  LANDING_POST_SERVICES,
  LANDING_BOTTOM,
} from '../../shared/data/landing-sections';

const FAQS: { q: string; a: string }[] = [
  {
    q: 'What does SEMNexus do?',
    a: 'SEMNexus is a full-service mobile app development and marketing agency. We combine custom app development, AI development, App Store Optimization (ASO), mobile app marketing, user acquisition, influencer marketing, and Answer Engine Optimization (AEO) under one roof, supporting the entire product lifecycle from idea validation to growth.',
  },
  {
    q: 'Does SEMNexus handle both app development and marketing?',
    a: 'Yes. Unlike agencies that focus on either development or marketing, SEMNexus does both. Clients work with a single partner capable of designing, building, launching, marketing, and scaling digital products, which reduces overhead and aligns product development with growth objectives.',
  },
  {
    q: 'What kinds of apps does SEMNexus build?',
    a: 'We build custom iOS, Android, cross-platform (React Native), SaaS, AI-powered, and web applications — from startup MVPs to enterprise platforms — with UI/UX design, API development, and full-lifecycle support.',
  },
  {
    q: 'Can SEMNexus help my brand show up in AI search tools like ChatGPT?',
    a: 'Yes. Our Answer Engine Optimization (AEO) services improve visibility within AI-generated recommendations across ChatGPT, Gemini, Claude, Perplexity, and Google AI Overviews through entity optimization, structured data, authority building, and citation development.',
  },
];

@Component({
  selector: 'app-landing',
  imports: [CommonModule, Background, HeroSection, Button, WhySemnexus, Services, DevServices, TagServices, LeadSection, CaseStudies, Team, PostCarouselContainer, Connect, ContentSection],
  templateUrl: './landing.html',
  styleUrl: './landing.scss'
})
export class Landing {

  @ViewChild('services') servicesSection!: ElementRef<HTMLElement>;
  @ViewChild('caseStudies') caseStudiesSection!: ElementRef<HTMLElement>;

  /** Left/editorial hero heading — two-tone, mirroring the service-page heros. */
  protected heroTitleHtml = '<span class="gradient-text">mobile app</span><br>development &<br><span class="gradient-text">marketing agency</span>';

  protected introSections = LANDING_INTRO;
  // Sections 0 + 1 render via bespoke components: "custom mobile app development" as an
  // icon-pill grid (<app-dev-services>), "mobile app marketing services" as pill tags
  // (<app-tag-services>). The remaining post-services sections keep the generic content-section.
  protected devSection = LANDING_POST_SERVICES[0];
  protected mktSection = LANDING_POST_SERVICES[1];
  protected aeoSection = LANDING_POST_SERVICES[2];
  // "why businesses choose SEMNexus" renders centered with check-pill tags.
  protected whyBusinessesSection = LANDING_POST_SERVICES[3];
  protected bottomSections = LANDING_BOTTOM;

  constructor(private route: ActivatedRoute,
    private seo: SeoService,
    private calendly: CalendlyService) { }

  scheduleCall() {
    this.calendly.openPopup().catch(() => { });
  }

  ngOnInit() {
    this.seo.apply({
      title: 'Mobile App Development & Marketing Agency',
      description: 'SEMNexus is a full-service mobile app development and marketing agency: custom iOS, Android, and AI apps plus ASO, user acquisition, influencer marketing, and Answer Engine Optimization to launch, scale, and grow digital products.',
      canonical: '/',
      image: 'https://semnexus.com/images/landing/backgrounds/landing-1.webp',
      imageAlt: 'SEM Nexus — App Development and App Marketing Agency',
      type: 'website',
    });
    this.seo.setJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://semnexus.com/#faq',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  goToSection(section: string) {
    if (section === 'services') {
      this.servicesSection.nativeElement.scrollIntoView({ behavior: 'smooth' });
    } else if (section === 'case-studies') {
      this.caseStudiesSection.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  ngAfterViewInit() {
    this.route.fragment.subscribe(fragment => {
      if (fragment === 'services') {
        setTimeout(() => this.goToSection('services'), 0);
      } else if (fragment === 'case-studies') {
        setTimeout(() => this.goToSection('case-studies'), 0);
      }
    });
  }

}
